// The guided tour on screen: the page dims around the element a step explains (a spotlight with a
// soft glowing ring), and a bubble floats next to it with the explanation, the step count and
// Back / Next / Skip. The page stays usable underneath, so you can try what you're shown.
// Keys: → / ← in the bubble step through, Esc ends the tour. Steps on other pages navigate there.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { navigate, usePath } from "../lib/router";
import { prefersCalm } from "../ui/settings";
import { useTour } from "./store";
import { TOURS, TOUR_NAMES, type TourStep } from "./tours";

const GAP = 14;
const MARGIN = 12;
const PAD = 6;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== "hidden";
}

/** The first visible element for the step's selectors (in their order). */
function findTarget(step: TourStep): HTMLElement | null {
  for (const sel of step.target ?? []) {
    for (const el of document.querySelectorAll<HTMLElement>(sel)) if (visible(el)) return el;
  }
  return null;
}

/** Where the bubble goes: below the target if it fits, else above, else beside, else the middle. */
export function placeBubble(target: Box | null, bubble: { w: number; h: number }, view: { w: number; h: number }) {
  const clampX = (x: number) => Math.max(MARGIN, Math.min(view.w - bubble.w - MARGIN, x));
  const clampY = (y: number) => Math.max(MARGIN, Math.min(view.h - bubble.h - MARGIN, y));
  if (!target) return { x: clampX((view.w - bubble.w) / 2), y: clampY((view.h - bubble.h) / 2), side: "none" as const };
  const cx = target.x + target.w / 2;
  if (target.y + target.h + GAP + bubble.h <= view.h - MARGIN)
    return { x: clampX(cx - bubble.w / 2), y: target.y + target.h + GAP, side: "below" as const };
  if (target.y - GAP - bubble.h >= MARGIN)
    return { x: clampX(cx - bubble.w / 2), y: target.y - GAP - bubble.h, side: "above" as const };
  const cy = clampY(target.y + target.h / 2 - bubble.h / 2);
  if (target.x + target.w + GAP + bubble.w <= view.w - MARGIN)
    return { x: target.x + target.w + GAP, y: cy, side: "right" as const };
  if (target.x - GAP - bubble.w >= MARGIN) return { x: target.x - GAP - bubble.w, y: cy, side: "left" as const };
  // A big target (e.g. the grid): float over its lower part.
  return { x: clampX(cx - bubble.w / 2), y: clampY(view.h - bubble.h - MARGIN), side: "none" as const };
}

export function TourOverlay() {
  const tour = useTour((s) => s.tour);
  const stepIndex = useTour((s) => s.step);
  const path = usePath();
  const steps = tour ? TOURS[tour] : [];
  const step = steps[stepIndex] as TourStep | undefined;
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [searching, setSearching] = useState(false);
  const [box, setBox] = useState<Box | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number; w: number; side: string } | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  // Go to the step's page, then find its element (pages and panels may take a moment to appear).
  useEffect(() => {
    if (!step) return;
    if (step.route && path !== step.route) {
      navigate(step.route);
      return;
    }
    setTarget(null);
    setBox(null);
    setSearching(true);
    let alive = true;
    const started = performance.now();
    const look = () => {
      if (!alive) return;
      const el = findTarget(step);
      if (el) {
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: prefersCalm() ? "auto" : "smooth" });
        setTarget(el);
        setSearching(false);
      } else if (performance.now() - started > 4000) setSearching(false);
      else setTimeout(look, 100);
    };
    look();
    return () => {
      alive = false;
    };
  }, [step, path]);

  // Follow the target while it moves (scrolling, layout changes).
  useEffect(() => {
    if (!target) return;
    let raf = 0;
    let last = "";
    const frame = () => {
      const r = target.getBoundingClientRect();
      const key = `${r.x}|${r.y}|${r.width}|${r.height}`;
      if (key !== last) {
        last = key;
        setBox({ x: r.x - PAD, y: r.y - PAD, w: r.width + 2 * PAD, h: r.height + 2 * PAD });
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  // Place the bubble once its size is known.
  useLayoutEffect(() => {
    const b = bubbleRef.current;
    if (!b || !step) return;
    // Only the visible part of the target counts for placement (big targets reach off screen).
    const vis = box && {
      x: Math.max(0, box.x),
      y: Math.max(0, box.y),
      w: Math.min(innerWidth, box.x + box.w) - Math.max(0, box.x),
      h: Math.min(innerHeight, box.y + box.h) - Math.max(0, box.y),
    };
    const size = { w: b.offsetWidth, h: b.offsetHeight };
    const at = placeBubble(vis && vis.w > 0 && vis.h > 0 ? vis : null, size, { w: innerWidth, h: innerHeight });
    setPos({ ...at, w: size.w });
  }, [box, step, searching]);

  // Focus the Next button once the step's bubble is placed (a hidden bubble can't take focus).
  const placed = pos !== null;
  const focusedStep = useRef<TourStep | null>(null);
  useEffect(() => {
    if (!step || !placed || focusedStep.current === step) return;
    focusedStep.current = step;
    nextRef.current?.focus({ preventScroll: true });
  }, [step, placed]);

  const stop = useTour((s) => s.stop);
  const go = useTour((s) => s.go);
  const next = useCallback(() => {
    if (stepIndex + 1 < steps.length) go(stepIndex + 1);
    else stop();
  }, [stepIndex, steps.length, go, stop]);
  const back = useCallback(() => stepIndex > 0 && go(stepIndex - 1), [stepIndex, go]);

  useEffect(() => {
    if (!tour) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stop();
        return;
      }
      if (!bubbleRef.current?.contains(document.activeElement)) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [tour, next, back, stop]);

  if (!tour || !step) return null;
  const last = stepIndex === steps.length - 1;
  const arrowX = box && pos ? Math.max(18, Math.min(box.x + box.w / 2 - pos.x, pos.w - 18)) : 0;
  return (
    <div
      className="tour"
      data-testid="tour"
      data-tour={tour}
      data-step={stepIndex}
      data-found={target ? "true" : searching ? "searching" : "false"}
    >
      {box ? (
        <div
          className="tour-spotlight"
          style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
          aria-hidden="true"
        />
      ) : (
        <div className="tour-dim" aria-hidden="true" />
      )}
      <div
        ref={bubbleRef}
        className={`tour-bubble side-${pos?.side ?? "none"}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
        style={pos ? { left: pos.x, top: pos.y } : { visibility: "hidden" }}
      >
        {box && pos && (pos.side === "below" || pos.side === "above") && (
          <span className="tour-arrow" style={{ left: arrowX }} aria-hidden="true" />
        )}
        <div className="tour-meta">
          <span>{TOUR_NAMES[tour]}</span>
          <span data-testid="tour-count">
            {stepIndex + 1} / {steps.length}
          </span>
        </div>
        <h2 id="tour-title">{step.title}</h2>
        <p id="tour-body">{step.body}</p>
        <div className="tour-dots" aria-hidden="true">
          {steps.map((_, i) => (
            <i key={i} className={i === stepIndex ? "on" : i < stepIndex ? "done" : ""} />
          ))}
        </div>
        <div className="tour-actions">
          <button type="button" className="tour-skip" onClick={stop} data-testid="tour-skip">
            {last ? "Close" : "Skip tour"}
          </button>
          <span className="spacer" />
          {stepIndex > 0 && (
            <button type="button" className="btn btn-small" onClick={back} data-testid="tour-back">
              ← Back
            </button>
          )}
          {step.action && (
            <button
              type="button"
              className="btn btn-small"
              onClick={() => {
                const t = step.action!.tour;
                stop();
                useTour.getState().start(t);
              }}
              data-testid="tour-action"
            >
              {step.action.label}
            </button>
          )}
          <button
            ref={nextRef}
            type="button"
            className="btn btn-small btn-primary"
            onClick={next}
            data-testid="tour-next"
          >
            {last ? "Done ✓" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}
